import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import styled, { css } from "styled-components";
import * as Immutable from "immutable";
import { useFormikContext } from "formik";
import fetch from "logic/rest/FetchProvider";
import URLUtils from "util/URLUtils";
import useTableLayout from "components/common/EntityDataTable/hooks/useTableLayout";
import useUpdateUserLayoutPreferences from "components/common/EntityDataTable/hooks/useUpdateUserLayoutPreferences";
import useFetchEntities from "components/common/PaginatedEntityTable/useFetchEntities";
import { useTableEventHandlers } from "components/common/EntityDataTable";
import { Spinner, PaginatedList, SearchForm, NoSearchResult, Pluralize } from "components/common";
import TableFetchContextProvider from "components/common/PaginatedEntityTable/TableFetchContextProvider";
import PaginationURL from "util/PaginationURL";
import { Table } from "components/bootstrap";
import Widget from "views/logic/widgets/Widget";
import Parameter from "views/logic/parameters/Parameter";
import ModalSubmit from "components/common/ModalSubmit";
import FiltersForQueryParams from "components/common/EntityFilters/FiltersForQueryParams";
import BulkSelectCheckBox from "report/report-creation/content/BulkSelectCheckbox";
import DashboardRow from "report/report-creation/content/DashboardRow";
import FiltersBar from "report/report-creation/content/filters/FiltersBar";
const DEFAULT_DISPLAYED_ATTRIBUTES = ["title", "widget_title", "owner"];
const DEFAULT_LAYOUT = {
  entityTableId: "report_widgets",
  defaultPageSize: 14,
  defaultSort: { attributeId: "title", direction: "asc" },
  defaultDisplayedAttributes: DEFAULT_DISPLAYED_ATTRIBUTES,
  defaultColumnOrder: DEFAULT_DISPLAYED_ATTRIBUTES
};
const availableWidgetsUrls = "/plugins/org.graylog.plugins.report/widgets/paginated";
const fetchAvailableWidgets = (searchParams) => {
  const url = PaginationURL(availableWidgetsUrls, searchParams.page, searchParams.pageSize, searchParams.query, {
    sort: searchParams.sort.attributeId,
    order: searchParams.sort.direction,
    filters: FiltersForQueryParams(searchParams.filters)
  });
  return fetch("GET", URLUtils.qualifyUrl(url)).then(
    ({ elements: list, ...rest }) => ({
      ...rest,
      list: list.map(({ widgets, ...dashboard }) => ({
        ...dashboard,
        widgets: widgets.map((widget) => ({
          id: widget.id,
          title: widget.title,
          type: widget.type,
          config: Widget.fromJSON(widget).config,
          parameters: widget.parameters.map((p) => Parameter.fromJSON(p)),
          eligible: widget.eligible
        }))
      }))
    })
  );
};
export const KEY_PREFIX = ["reports", "widgets"];
export const keyFn = (searchParams) => [...KEY_PREFIX, searchParams];
const INITIAL_DATA = {
  pagination: { total: 0 },
  list: [],
  attributes: []
};
const SearchRow = styled.div`
  margin-bottom: 5px;
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
`;
const humanName = "Report Widgets";
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;
const TableContainer = styled.div`
  flex: 1;
  overflow: auto;
`;
const HorizontalBar = styled.div(
  ({ theme }) => css`
    width: 100%;
    border-top: ${theme.colors.gray[90]} 2px solid;
    margin: 5px 15px 10px -15px;
  `
);
const AddWidgetForm = ({ onClose, onSubmit, widgets: disabledWidgets }) => {
  const { isSubmitting } = useFormikContext();
  const [urlQueryFilters, setUrlQueryFilters] = useState(Immutable.OrderedMap());
  const [query, setQuery] = useState(void 0);
  const { layoutConfig, isInitialLoading: isLoadingLayoutPreferences } = useTableLayout(DEFAULT_LAYOUT);
  const [pagination, setPagination] = useState({ page: 1, pageSize: layoutConfig.pageSize });
  const { mutateAsync: updateTableLayout } = useUpdateUserLayoutPreferences(DEFAULT_LAYOUT.entityTableId);
  const fetchOptions = useMemo(
    () => ({
      query,
      page: pagination.page,
      pageSize: layoutConfig.pageSize,
      sort: layoutConfig.sort,
      filters: urlQueryFilters
    }),
    [layoutConfig.pageSize, layoutConfig.sort, pagination.page, query, urlQueryFilters]
  );
  const fetchKey = useMemo(() => keyFn(fetchOptions), [fetchOptions]);
  const resetPage = useCallback(() => setPagination((prev) => ({ ...prev, page: 1 })), []);
  const {
    data: paginatedEntities = INITIAL_DATA,
    isInitialLoading: isLoadingEntities,
    refetch
  } = useFetchEntities({
    fetchKey,
    searchParams: fetchOptions,
    enabled: !isLoadingLayoutPreferences,
    fetchEntities: fetchAvailableWidgets,
    humanName
  });
  const onChangeFilters = useCallback(
    (newUrlQueryFilters) => {
      resetPage();
      setUrlQueryFilters(newUrlQueryFilters);
    },
    [resetPage]
  );
  const [widgetSelection, setWidgetSelection] = useState([]);
  const _onSubmit = useCallback(
    () => onSubmit(disabledWidgets.concat(widgetSelection)),
    [disabledWidgets, onSubmit, widgetSelection]
  );
  const { onSearch, onSearchReset } = useTableEventHandlers({
    appSection: `${DEFAULT_LAYOUT.entityTableId}-list`,
    paginationQueryParameter: {
      ...pagination,
      resetPage,
      setPagination: (payload) => setPagination((prev) => ({ ...prev, ...payload }))
    },
    updateTableLayout,
    setQuery
  });
  if (isLoadingLayoutPreferences || isLoadingEntities) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  const {
    list,
    pagination: { total },
    attributes
  } = paginatedEntities;
  return /* @__PURE__ */ React.createElement(Container, null, /* @__PURE__ */ React.createElement(
    TableFetchContextProvider,
    {
      refetch,
      searchParams: fetchOptions,
      attributes,
      entityTableId: DEFAULT_LAYOUT.entityTableId
    },
    /* @__PURE__ */ React.createElement(
      PaginatedList,
      {
        pageSize: pagination.pageSize,
        activePage: pagination.page,
        useQueryParameter: false,
        showPageSizeSelect: false,
        totalItems: total,
        onChange: (newPage, newPageSize) => setPagination({ page: newPage, pageSize: newPageSize })
      },
      /* @__PURE__ */ React.createElement(FiltersBar, { attributes, filters: urlQueryFilters, setFilters: onChangeFilters }),
      /* @__PURE__ */ React.createElement(HorizontalBar, null),
      /* @__PURE__ */ React.createElement(SearchRow, null, /* @__PURE__ */ React.createElement(SearchForm, { onSearch, onReset: onSearchReset, query })),
      /* @__PURE__ */ React.createElement(TableContainer, null, (list == null ? void 0 : list.length) === 0 ? /* @__PURE__ */ React.createElement(NoSearchResult, null, "No dashboards/widgets have been found.") : /* @__PURE__ */ React.createElement(Table, null, /* @__PURE__ */ React.createElement("thead", null, /* @__PURE__ */ React.createElement("tr", null, /* @__PURE__ */ React.createElement("th", null, /* @__PURE__ */ React.createElement(
        BulkSelectCheckBox,
        {
          dashboards: list,
          onChange: setWidgetSelection,
          selected: widgetSelection,
          disabledWidgets
        }
      )), /* @__PURE__ */ React.createElement("th", null), /* @__PURE__ */ React.createElement("th", null, "Name"), /* @__PURE__ */ React.createElement("th", null, "Created by"), /* @__PURE__ */ React.createElement("th", null, "Last Update"))), /* @__PURE__ */ React.createElement("tbody", null, list.map((dashboard) => /* @__PURE__ */ React.createElement(
        DashboardRow,
        {
          key: `dashboard-${dashboard.id}`,
          dashboard,
          checked: widgetSelection,
          widgetsInReport: disabledWidgets,
          onChange: setWidgetSelection
        }
      )))))
    )
  ), /* @__PURE__ */ React.createElement(
    ModalSubmit,
    {
      submitButtonText: /* @__PURE__ */ React.createElement(React.Fragment, null, "Add ", widgetSelection.length !== 0 && `${widgetSelection.length} `, /* @__PURE__ */ React.createElement(Pluralize, { singular: "widget", plural: "widgets", value: widgetSelection.length })),
      displayCancel: true,
      onCancel: onClose,
      onSubmit: _onSubmit,
      disabledSubmit: widgetSelection.length === 0,
      isAsyncSubmit: true,
      isSubmitting,
      submitLoadingText: "Adding widgets ..."
    }
  ));
};
export default AddWidgetForm;
